import { useEffect, useMemo, useState } from 'react'
import { Spinner } from '../../../components/ui/Spinner'
import { selectClass, inputClass } from '../../../components/ui/FormField'
import { api } from '../../../api/axios'
import { useCampeonatos } from '../../campeonatos/hooks/useCampeonatos'
import { DataBR } from '../../../components/ui/DataBR'
import { dataLocalISO } from '../../../lib/dataLocal'

// Anexo E de todos os participantes de um campeonato: gera e envia por e-mail (anexo)
// e WhatsApp (documento) de uma vez. O envio roda na API; a tela acompanha pelo id.

interface Participante {
  idatirador: number
  nome: string
  email: string | null
  telefone: string | null
  whatsapp_valido: boolean
  inscricoes: number
}

type Situacao = 'pendente' | 'enviado' | 'sem_contato' | 'falha'

interface Lote {
  id: string
  total: number
  processados: number
  concluido: boolean
  itens: Array<{
    idatirador: number; nome: string; arquivo: string | null; erro_pdf: string | null
    email: Situacao; email_erro: string | null; whatsapp: Situacao; whatsapp_erro: string | null
  }>
}

const ROTULO: Record<Situacao, string> = { pendente: '...', enviado: 'enviado', sem_contato: 'sem contato', falha: 'falha' }
const COR: Record<Situacao, string> = {
  pendente: 'text-compumaq-textoSuave', enviado: 'text-green-600', sem_contato: 'text-amber-600', falha: 'text-red-600',
}

function hojeIso(): string { return dataLocalISO() }
function haUmAno(): string { const d = new Date(); d.setFullYear(d.getFullYear() - 1); return dataLocalISO(d) }

type TipoLote = 'anexo_e' | 'frequencia'
const NOME_TIPO: Record<TipoLote, string> = { anexo_e: 'Anexo E (Comprovacao de Calendario)', frequencia: 'Declaracao de Frequencia' }

export function EnvioLoteAnexoE({ onAlert }: { onAlert: (a: { type: 'success' | 'error'; message: string }) => void }) {
  const { data: campeonatosData } = useCampeonatos({ limit: 200 })
  const [idcampeonato, setIdcampeonato] = useState<number | ''>('')
  const [tipo, setTipo] = useState<TipoLote>('anexo_e')
  const [dataInicio, setDataInicio] = useState(haUmAno())
  const [dataFim, setDataFim] = useState(hojeIso())
  const [participantes, setParticipantes] = useState<Participante[]>([])
  const [carregando, setCarregando] = useState(false)
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set())
  const [estadoWpp, setEstadoWpp] = useState<string | null>(null)
  const [lote, setLote] = useState<Lote | null>(null)
  const [iniciando, setIniciando] = useState(false)
  const [baixando, setBaixando] = useState(false)

  useEffect(() => {
    api.get<{ data: { estado: string } }>('/atiradores/whatsapp/estado')
      .then(r => setEstadoWpp(r.data.data.estado))
      .catch(() => setEstadoWpp('erro'))
  }, [])

  // participantes do campeonato no periodo; todos ja marcados
  useEffect(() => {
    setParticipantes([]); setSelecionados(new Set())
    if (!idcampeonato || !dataInicio || !dataFim) return
    let ativo = true
    setCarregando(true)
    api.get<{ data: Participante[] }>('/relatorios/declaracoes-lote/participantes', { params: { idcampeonato, data_inicio: dataInicio, data_fim: dataFim } })
      .then(r => {
        if (!ativo) return
        setParticipantes(r.data.data)
        setSelecionados(new Set(r.data.data.map(p => p.idatirador)))
      })
      .catch(err => ativo && onAlert({ type: 'error', message: err?.response?.data?.error || 'Erro ao buscar os participantes' }))
      .finally(() => ativo && setCarregando(false))
    return () => { ativo = false }
  }, [idcampeonato, dataInicio, dataFim])

  // acompanha o envio em segundo plano
  const loteId = lote && !lote.concluido ? lote.id : null
  useEffect(() => {
    if (!loteId) return
    const t = setInterval(async () => {
      try {
        const { data } = await api.get<{ data: Lote }>(`/relatorios/declaracoes-lote/${loteId}`)
        setLote(data.data)
        if (data.data.concluido) {
          const it = data.data.itens
          const em = it.filter(i => i.email === 'enviado').length
          const wp = it.filter(i => i.whatsapp === 'enviado').length
          const fa = it.filter(i => i.email === 'falha' || i.whatsapp === 'falha').length
          onAlert({ type: fa ? 'error' : 'success', message: `Envio concluido: ${em} e-mail(s) e ${wp} WhatsApp enviado(s)${fa ? `, ${fa} com falha` : ''}.` })
        }
      } catch (err: any) {
        if (err?.response?.status === 404) {
          setLote(prev => prev && { ...prev, concluido: true })
          onAlert({ type: 'error', message: 'Perdeu o acompanhamento do envio (a API reiniciou?). Confira quem recebeu.' })
        }
      }
    }, 3000)
    return () => clearInterval(t)
  }, [loteId])

  const escolhidos = useMemo(() => participantes.filter(p => selecionados.has(p.idatirador)), [participantes, selecionados])
  const comEmail = escolhidos.filter(p => p.email).length
  const comWpp = escolhidos.filter(p => p.whatsapp_valido).length
  const enviando = iniciando || (lote !== null && !lote.concluido)
  const todos = participantes.length > 0 && participantes.every(p => selecionados.has(p.idatirador))

  function toggle(id: number) {
    setSelecionados(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n })
  }

  async function handleEnviar() {
    if (!idcampeonato || !escolhidos.length) return
    const minutos = Math.max(1, Math.ceil((comWpp * 5.5) / 60))
    if (!window.confirm(`Gerar a ${NOME_TIPO[tipo]} de ${escolhidos.length} participante(s) e enviar?\n\n` +
      `E-mail: ${comEmail}  |  WhatsApp: ${comWpp}\n\nLeva uns ${minutos} min (pausa entre as mensagens do WhatsApp). Deixe esta tela aberta para ver o resultado.`)) return
    setIniciando(true)
    try {
      const { data } = await api.post<{ data: Lote }>('/relatorios/declaracoes-lote', {
        idcampeonato, idatiradores: escolhidos.map(p => p.idatirador), data_inicio: dataInicio, data_fim: dataFim, tipo,
      })
      setLote(data.data)
    } catch (err: any) {
      onAlert({ type: 'error', message: err?.response?.data?.error || 'Erro ao iniciar o envio' })
    } finally { setIniciando(false) }
  }

  async function handleZip() {
    if (!lote) return
    setBaixando(true)
    try {
      const resp = await api.get<Blob>(`/relatorios/declaracoes-lote/${lote.id}/zip`, { responseType: 'blob' })
      const url = URL.createObjectURL(resp.data)
      const a = document.createElement('a')
      a.href = url; a.download = tipo === 'frequencia' ? 'declaracoes_frequencia.zip' : 'declaracoes_anexo_e.zip'
      document.body.appendChild(a); a.click(); document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch {
      onAlert({ type: 'error', message: 'Erro ao baixar o ZIP' })
    } finally { setBaixando(false) }
  }

  return (
    <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-6 shadow-sm space-y-4">
      <h2 className="font-semibold text-compumaq-texto">Envio em lote — declaracao para todos os participantes</h2>
      <p className="text-sm text-compumaq-textoSuave">
        Gera a declaracao escolhida de cada participante do campeonato no periodo e envia o PDF por e-mail (anexo) e
        WhatsApp (documento). As armas saem como estiverem vinculadas nas inscricoes.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div>
          <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Declaracao</label>
          <select className={selectClass} aria-label="Tipo do lote" value={tipo} disabled={enviando}
            onChange={e => { setTipo(e.target.value as TipoLote); setLote(null) }}>
            <option value="anexo_e">{NOME_TIPO.anexo_e}</option>
            <option value="frequencia">{NOME_TIPO.frequencia}</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Campeonato</label>
          <select className={selectClass} aria-label="Campeonato do lote" value={idcampeonato} disabled={enviando}
            onChange={e => { setIdcampeonato(e.target.value ? parseInt(e.target.value, 10) : ''); setLote(null) }}>
            <option value="">Selecione...</option>
            {campeonatosData?.data?.map(c => (
              <option key={c.idcampeonato} value={c.idcampeonato}>{c.descricao} — {c.temporada}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Data de inicio</label>
          <DataBR className={inputClass} aria-label="Inicio do lote" value={dataInicio} max={dataFim} disabled={enviando}
            onChange={e => setDataInicio(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">Data de fim</label>
          <DataBR className={inputClass} aria-label="Fim do lote" value={dataFim} min={dataInicio} disabled={enviando}
            onChange={e => setDataFim(e.target.value)} />
        </div>
      </div>

      <p className="text-sm text-compumaq-textoSuave">
        WhatsApp do campeonato:{' '}
        {estadoWpp === null ? '...' : estadoWpp === 'open'
          ? <span className="text-green-600 font-medium">conectado</span>
          : <span className="text-red-600">desconectado — conecte em Convites de Inscricao antes de enviar</span>}
      </p>

      {carregando ? (
        <div className="flex justify-center py-8"><Spinner size="lg" /></div>
      ) : idcampeonato && !participantes.length ? (
        <p className="text-sm text-compumaq-textoSuave">Nenhum participante neste campeonato no periodo.</p>
      ) : participantes.length > 0 && (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm text-compumaq-textoSuave">
              {escolhidos.length} de {participantes.length} selecionado(s) — e-mail: {comEmail}, WhatsApp: {comWpp}
            </span>
            <button onClick={handleEnviar} disabled={enviando || !escolhidos.length || estadoWpp !== 'open'}
              className="px-5 py-2.5 bg-compumaq-dourado text-compumaq-texto rounded-lg text-sm font-medium hover:bg-compumaq-doradoClaro disabled:opacity-50 flex items-center gap-2">
              {enviando && <Spinner size="sm" />}
              {lote && !lote.concluido ? `Enviando ${lote.processados}/${lote.total}...` : iniciando ? 'Iniciando...' : `Gerar e enviar (${escolhidos.length})`}
            </button>
            {lote?.concluido && (
              <button onClick={handleZip} disabled={baixando}
                className="px-4 py-2.5 border border-compumaq-dourado/50 text-compumaq-texto rounded-lg text-sm hover:bg-compumaq-preto disabled:opacity-50">
                {baixando ? 'Baixando...' : 'Baixar todas (ZIP)'}
              </button>
            )}
          </div>

          {lote && (
            <div className="h-2 bg-compumaq-preto rounded-full overflow-hidden">
              <div className="h-full bg-green-600 transition-all" style={{ width: `${Math.round((lote.processados / lote.total) * 100)}%` }} />
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-compumaq-preto border-b border-compumaq-dourado/25">
                <tr>
                  <th className="px-3 py-2 w-10">
                    <input type="checkbox" checked={todos} disabled={enviando}
                      onChange={() => setSelecionados(todos ? new Set() : new Set(participantes.map(p => p.idatirador)))} />
                  </th>
                  <th className="text-left px-3 py-2 font-medium text-compumaq-textoSuave">Atirador</th>
                  <th className="text-left px-3 py-2 font-medium text-compumaq-textoSuave">E-mail</th>
                  <th className="text-left px-3 py-2 font-medium text-compumaq-textoSuave">WhatsApp</th>
                  {lote && <th className="text-left px-3 py-2 font-medium text-compumaq-textoSuave">Envio</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-compumaq-dourado/25">
                {participantes.map(p => {
                  const it = lote?.itens.find(i => i.idatirador === p.idatirador)
                  return (
                    <tr key={p.idatirador} data-atirador={p.idatirador}>
                      <td className="px-3 py-2">
                        <input type="checkbox" checked={selecionados.has(p.idatirador)} disabled={enviando} onChange={() => toggle(p.idatirador)} />
                      </td>
                      <td className="px-3 py-2 text-compumaq-texto">{p.nome}</td>
                      <td className="px-3 py-2 text-compumaq-textoSuave">{p.email ?? <span className="text-amber-600">sem e-mail</span>}</td>
                      <td className="px-3 py-2 text-compumaq-textoSuave">
                        {p.whatsapp_valido ? p.telefone : <span className="text-amber-600">{p.telefone ? 'telefone invalido' : 'sem telefone'}</span>}
                      </td>
                      {lote && (
                        <td className="px-3 py-2 text-xs">
                          {!it ? <span className="text-compumaq-textoSuave">nao enviado</span> : it.erro_pdf
                            ? <span className="text-red-600">PDF: {it.erro_pdf}</span>
                            : <>
                                <span className={COR[it.email]} title={it.email_erro ?? undefined}>e-mail {ROTULO[it.email]}</span>
                                {' · '}
                                <span className={COR[it.whatsapp]} title={it.whatsapp_erro ?? undefined}>WhatsApp {ROTULO[it.whatsapp]}</span>
                                {(it.email_erro || it.whatsapp_erro) && (
                                  <span className="block text-red-600">{[it.email_erro, it.whatsapp_erro].filter(Boolean).join('; ')}</span>
                                )}
                              </>}
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
